/**
 * UwEntscheidDialog - record the carrier response (annehmen / ablehnen) from the claimed reply
 * (P2 Slice 14 D2). The broker reads the mail (deep link) and sets the outcome; ablehnen needs a Grund.
 */

import { useEffect, useState } from 'react';
import { Dialog } from '../administration/components';
import { Button } from '../../components/ui/Button';
import { dateTimeShortDe } from '../../lib/format';
import type { UnderwritingResponse, UwEntscheidBody } from '@brokly/shared';

const inputCls = 'w-full rounded-md border bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const ERROR_TEXT: Record<string, string> = {
  grund_required: 'Bitte Begruendung angeben',
  antrag_status_invalid: 'Der Antrag ist nicht mehr in diesem Status',
  placement_locked: 'Die Ausschreibung ist abgeschlossen',
};

export function UwEntscheidDialog({
  open,
  onClose,
  art,
  facts,
  onSubmit,
  saving,
  error,
}: {
  open: boolean;
  onClose: () => void;
  art: 'annehmen' | 'ablehnen';
  facts: UnderwritingResponse;
  onSubmit: (body: UwEntscheidBody) => void;
  saving: boolean;
  error: string | null;
}) {
  const [grund, setGrund] = useState('');
  useEffect(() => {
    if (open) setGrund('');
  }, [open, art]);

  const reply = facts.carrier_reply;
  const grundOk = art === 'annehmen' || grund.trim().length >= 3;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={art === 'annehmen' ? 'Annahme erfassen' : 'Ablehnung erfassen'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Zurueck</Button>
          <Button
            variant={art === 'ablehnen' ? 'destructive' : 'default'}
            onClick={() => onSubmit({ grund: grund.trim() || null, communication_id: reply?.communication_id ?? null })}
            disabled={saving || !grundOk}
            data-testid="uw-entscheid-submit"
          >
            {art === 'annehmen' ? 'Annahme erfassen' : 'Ablehnung erfassen'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="uw-entscheid-dialog">
        {reply && (
          <p className="text-sm text-muted-foreground">
            Rueckmeldung: {dateTimeShortDe(reply.received_at)}{' '}
            <a href={`/posteingang/${reply.communication_id}`} className="underline" data-testid="uw-entscheid-mail-link">Mail oeffnen</a>
          </p>
        )}
        <label className="text-sm">
          Begruendung / Wortlaut des Versicherers{art === 'ablehnen' ? ' (erforderlich)' : ''}
          <textarea
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            rows={4}
            className={inputCls}
            data-testid="uw-entscheid-grund"
          />
        </label>
        {error && <p role="alert" className="text-sm text-status-danger" data-testid="uw-entscheid-error">{ERROR_TEXT[error] ?? error}</p>}
      </div>
    </Dialog>
  );
}
